package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.HFButton
import com.mgacreative.hotel.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotel.shared.ui.designsystem.components.HFTextField
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun KbsSettingsScreen(
    onNavigateBack: () -> Unit = {}
) {
    val viewModel = koinViewModel<AdminViewModel>()
    val profile by viewModel.profile.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadDashboardData()
    }

    var provider by remember(profile) { mutableStateOf(profile?.kbsProvider ?: "EGM") }
    var facilityCode by remember(profile) { mutableStateOf(profile?.kbsFacilityCode ?: "") }
    var username by remember(profile) { mutableStateOf(profile?.kbsUsername ?: "") }
    var password by remember(profile) { mutableStateOf(profile?.kbsPassword ?: "") }
    var autoSend by remember(profile) { mutableStateOf(profile?.kbsAutoSend ?: false) }
    var passwordVisible by remember { mutableStateOf(false) }

    var testStatusMessage by remember { mutableStateOf<Pair<Boolean, String>?>(null) }
    var isTestingConnection by remember { mutableStateOf(false) }
    var saveResultMessage by remember { mutableStateOf<Pair<Boolean, String>?>(null) }

    val scrollState = rememberScrollState()

    Scaffold(
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            text = l10n("KBS Kimlik Bildirim Ayarları"),
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp
                        )
                        Text(
                            text = l10n("Emniyet (AKBS) & Jandarma (JKBS) Web Servis Entegrasyonu"),
                            fontSize = 12.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.White
                )
            )
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .background(Color(0xFFF8FAFC)),
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 1000.dp)
                    .verticalScroll(scrollState)
                    .verticalScrollbar(scrollState)
                    .padding(24.dp),
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                // Bilgi / Yasal Sorumluluk Kartı
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFEFF6FF)),
                    border = CardDefaults.outlinedCardBorder().copy(brush = androidx.compose.ui.graphics.SolidColor(Color(0xFFBFDBFE)))
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(44.dp)
                                .clip(CircleShape)
                                .background(Color(0xFF2563EB)),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(Icons.Default.Security, contentDescription = null, tint = Color.White)
                        }
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = "1774 Sayılı Kimlik Bildirme Kanunu Uyumluluğu",
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF1E3A8A),
                                fontSize = 14.sp
                            )
                            Spacer(Modifier.height(4.dp))
                            Text(
                                text = "Konaklayan tüm yerli ve yabancı misafirlerin kimlik ve giriş/çıkış verileri, kolluk kuvvetleri KBS sistemine anlık veya gün sonuna kadar iletilmelidir. Tesis bilgilerinizi eksiksiz kaydediniz.",
                                fontSize = 12.sp,
                                color = Color(0xFF3B82F6),
                                lineHeight = 16.sp
                            )
                        }
                    }
                }

                // 1. Kurum / Sağlayıcı Seçimi
                HFCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Text(
                            text = "1. Kolluk Kuvveti & Bildirim Mercii",
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF1E293B)
                        )
                        Spacer(Modifier.height(4.dp))
                        Text(
                            text = "Tesisinizin bağlı olduğu kolluk birimini seçin.",
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                        Spacer(Modifier.height(16.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            FilterChip(
                                selected = provider == "EGM",
                                onClick = { provider = "EGM" },
                                label = {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        Icon(Icons.Default.LocalPolice, contentDescription = null, modifier = Modifier.size(18.dp))
                                        Text("Emniyet Genel Müdürlüğü (AKBS)")
                                    }
                                },
                                modifier = Modifier.weight(1f)
                            )
                            FilterChip(
                                selected = provider == "JANDARMA",
                                onClick = { provider = "JANDARMA" },
                                label = {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        Icon(Icons.Default.Shield, contentDescription = null, modifier = Modifier.size(18.dp))
                                        Text("Jandarma Genel Komutanlığı (JKBS)")
                                    }
                                },
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                }

                // 2. Kimlik & Web Servis Bilgileri
                HFCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Text(
                            text = "2. Tesis Kimlik ve Web Servis Bilgileri",
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF1E293B)
                        )
                        Spacer(Modifier.height(4.dp))
                        Text(
                            text = "Kolluk kuvvetlerince tesisinize tahsis edilen resmi kod ve şifreleri girin.",
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                        Spacer(Modifier.height(16.dp))

                        HFTextField(
                            value = facilityCode,
                            onValueChange = { facilityCode = it.trim().uppercase() },
                            label = "Resmi Tesis Kodu (Örn: TESIS-340192)",
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(Modifier.height(12.dp))

                        HFTextField(
                            value = username,
                            onValueChange = { username = it.trim() },
                            label = "KBS Kullanıcı Adı / Tesis Kullanıcı Kodu",
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(Modifier.height(12.dp))

                        OutlinedTextField(
                            value = password,
                            onValueChange = { password = it },
                            label = { Text("KBS Web Servis Şifresi / Parola") },
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            trailingIcon = {
                                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                    Icon(
                                        imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                                        contentDescription = "Şifreyi Göster"
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(8.dp),
                            singleLine = true
                        )
                    }
                }

                // 3. Bildirim & Çalışma Modu (Hibrit)
                HFCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.padding(20.dp)) {
                        Text(
                            text = "3. Bildirim & Otomasyon Yöntemi (Hibrit)",
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF1E293B)
                        )
                        Spacer(Modifier.height(4.dp))
                        Text(
                            text = "Sistem kontrolü elinizde tutan güvenli hibrit model ile çalışır.",
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                        Spacer(Modifier.height(16.dp))

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFF1F5F9), RoundedCornerShape(10.dp))
                                .padding(14.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Default.Check, contentDescription = null, tint = Color(0xFF059669), modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Check-In sonrası anlık onay penceresi ile tek tıkla gönderim", fontSize = 12.sp, color = Color(0xFF334155))
                            }
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Default.Check, contentDescription = null, tint = Color(0xFF059669), modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Giriş Yapacaklar listesinde misafir kartından bağımsız manuel bildirim", fontSize = 12.sp, color = Color(0xFF334155))
                            }
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Default.Check, contentDescription = null, tint = Color(0xFF059669), modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Yoğun saatlerde veya gün sonunda tek tıkla Toplu KBS Gönderimi", fontSize = 12.sp, color = Color(0xFF334155))
                            }
                        }

                        Spacer(Modifier.height(14.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "Tam Otomatik Arka Plan Gönderimi",
                                    fontWeight = FontWeight.Medium,
                                    fontSize = 13.sp
                                )
                                Text(
                                    text = "Aktif edildiğinde check-in anında onay beklemeden doğrudan servise yollar.",
                                    fontSize = 11.sp,
                                    color = Color(0xFF64748B)
                                )
                            }
                            Switch(
                                checked = autoSend,
                                onCheckedChange = { autoSend = it }
                            )
                        }
                    }
                }

                // 4. Bağlantı Testi ve Durum Bildirimi
                if (testStatusMessage != null) {
                    val isSuccess = testStatusMessage?.first == true
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            containerColor = if (isSuccess) Color(0xFFECFDF5) else Color(0xFFFEF2F2)
                        ),
                        border = BorderStroke(1.dp, if (isSuccess) Color(0xFF6EE7B7) else Color(0xFFFCA5A5))
                    ) {
                        Row(
                            modifier = Modifier.padding(14.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Icon(
                                imageVector = if (isSuccess) Icons.Default.CheckCircle else Icons.Default.Error,
                                contentDescription = null,
                                tint = if (isSuccess) Color(0xFF059669) else Color(0xFFDC2626)
                            )
                            Text(
                                text = testStatusMessage?.second ?: "",
                                fontSize = 13.sp,
                                color = if (isSuccess) Color(0xFF065F46) else Color(0xFF991B1B)
                            )
                        }
                    }
                }

                if (saveResultMessage != null) {
                    val isSuccess = saveResultMessage?.first == true
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            containerColor = if (isSuccess) Color(0xFFECFDF5) else Color(0xFFFEF2F2)
                        ),
                        border = BorderStroke(1.dp, if (isSuccess) Color(0xFF6EE7B7) else Color(0xFFFCA5A5))
                    ) {
                        Row(
                            modifier = Modifier.padding(14.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Icon(
                                imageVector = if (isSuccess) Icons.Default.CheckCircle else Icons.Default.Error,
                                contentDescription = null,
                                tint = if (isSuccess) Color(0xFF059669) else Color(0xFFDC2626)
                            )
                            Text(
                                text = saveResultMessage?.second ?: "",
                                fontSize = 13.sp,
                                color = if (isSuccess) Color(0xFF065F46) else Color(0xFF991B1B)
                            )
                        }
                    }
                }

                // Alt Aksiyon Butonları
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    OutlinedButton(
                        onClick = {
                            isTestingConnection = true
                            viewModel.testKbsConnection(facilityCode, username) { success, msg ->
                                isTestingConnection = false
                                testStatusMessage = Pair(success, msg)
                            }
                        },
                        modifier = Modifier.weight(1f).height(48.dp),
                        shape = RoundedCornerShape(10.dp),
                        enabled = !isTestingConnection && facilityCode.isNotBlank() && username.isNotBlank()
                    ) {
                        if (isTestingConnection) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                            Spacer(Modifier.width(8.dp))
                        } else {
                            Icon(Icons.Default.CloudSync, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                        }
                        Text("Bağlantıyı Test Et", fontWeight = FontWeight.SemiBold)
                    }

                    Button(
                        onClick = {
                            saveResultMessage = null
                            viewModel.updateKbsSettings(
                                provider = provider,
                                facilityCode = facilityCode,
                                username = username,
                                password = password,
                                autoSend = autoSend
                            ) { success, msg ->
                                saveResultMessage = Pair(success, msg)
                            }
                        },
                        modifier = Modifier.weight(1.2f).height(48.dp),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1E3A8A)),
                        enabled = !isLoading
                    ) {
                        if (isLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                            Spacer(Modifier.width(8.dp))
                        } else {
                            Icon(Icons.Default.Save, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                        }
                        Text("KBS Ayarlarını Kaydet", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}
